import { useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Me, NotificationPrefs, Preferences, PreferencesPatch, UserSettings } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { useSavePrefs, usePrefs } from "../../lib/prefs";
import { SettingsRow } from "../../components/ui/Settings";
import { Switch } from "../../components/ui/Controls";
import { toast } from "../../components/ui/Toast";
import { useAuth } from "../auth/AuthProvider";
import { comboFromEvent, comboParts } from "./keybinds";

export type SettingsPatch = Partial<Omit<UserSettings, "notificationPrefs">> & { notificationPrefs?: Partial<NotificationPrefs> };

/** Optimistic PATCH /api/me/settings; rolls back and toasts on failure. */
export function useUpdateSettings() {
  const qc = useQueryClient();
  const { setMe } = useAuth();
  return async (patch: SettingsPatch) => {
    const prev = qc.getQueryData<Me | null>(["me"]);
    if (prev) {
      setMe({
        ...prev,
        settings: { ...prev.settings, ...patch, notificationPrefs: { ...prev.settings.notificationPrefs, ...(patch.notificationPrefs ?? {}) } },
      });
    }
    try {
      setMe(await api.patch<Me>("/api/me/settings", patch));
    } catch (e) {
      if (prev) setMe(prev);
      toast.error(errorMessage(e, "Couldn't save that setting"));
    }
  };
}

export type BooleanPref = { [K in keyof Preferences]: Preferences[K] extends boolean ? K : never }[keyof Preferences];

/** A row with a switch bound to a boolean preference. */
export function PrefSwitch({ k, title, description, disabled }: { k: BooleanPref; title: string; description?: ReactNode; disabled?: boolean }) {
  const prefs = usePrefs();
  const save = useSavePrefs();
  const id = `pref-${k}`;
  return (
    <SettingsRow title={title} description={description} htmlFor={id} disabled={disabled}>
      <Switch id={id} label={title} checked={prefs[k]} disabled={disabled} onCheckedChange={(v) => void save({ [k]: v } as PreferencesPatch)} />
    </SettingsRow>
  );
}

/** Lay groups out in two columns when the content area is wide enough. */
export function Columns({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className="@container">
      <div className={cn("grid items-start gap-x-8 @[56rem]:grid-cols-2", className)}>{children}</div>
    </div>
  );
}

export function Kbd({ combo, className }: { combo: string; className?: string }) {
  const parts = comboParts(combo);
  return (
    <span className={cn("inline-flex items-center gap-1", className)}>
      {parts.map((p, i) => (
        <kbd key={i} className="inline-flex h-6 min-w-6 items-center justify-center rounded border border-line-strong bg-surface px-1.5 font-sans text-xs font-medium text-fg-2">
          {p}
        </kbd>
      ))}
    </span>
  );
}

/** Static key chips for a literal sequence, e.g. ["Ctrl", "Enter"]. */
export function Keys({ keys }: { keys: string[] }) {
  return (
    <span className="inline-flex items-center gap-1">
      {keys.map((k, i) => (
        <kbd key={i} className="inline-flex h-6 min-w-6 items-center justify-center rounded border border-line-strong bg-surface px-1.5 font-sans text-xs font-medium text-fg-2">
          {k}
        </kbd>
      ))}
    </span>
  );
}

/**
 * Click to record a key combo. Esc cancels; Backspace clears (when `clearable`).
 * Captures keys before the app's global shortcut handler sees them.
 */
export function KeyRecorder({ value, onChange, label, clearable = true, invalid }: { value: string; onChange: (combo: string) => void; label: string; clearable?: boolean; invalid?: boolean }) {
  const [recording, setRecording] = useState(false);

  useEffect(() => {
    if (!recording) return;
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (e.key === "Escape") return setRecording(false);
      const bare = !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey;
      if (clearable && bare && (e.key === "Backspace" || e.key === "Delete")) {
        onChange("");
        return setRecording(false);
      }
      const combo = comboFromEvent(e);
      if (!combo) return;
      onChange(combo);
      setRecording(false);
    };
    const stop = () => setRecording(false);
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("blur", stop);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("blur", stop);
    };
  }, [recording, clearable, onChange]);

  return (
    <button
      type="button"
      onClick={() => setRecording((r) => !r)}
      onBlur={() => setRecording(false)}
      aria-label={recording ? `Recording shortcut for ${label}. Press keys, or Escape to cancel` : `${label}: ${value ? comboParts(value).join(" ") : "not set"}. Change`}
      className={cn(
        "inline-flex h-8 min-w-24 items-center justify-center rounded-md border px-2 text-sm transition-colors",
        recording ? "border-accent bg-accent-soft text-fg" : invalid ? "border-danger text-fg hover:bg-surface" : "border-line-strong text-fg-2 hover:bg-surface",
      )}
    >
      {recording ? <span className="text-xs">Press keys…</span> : value ? <Kbd combo={value} /> : <span className="text-xs text-fg-3">Not set</span>}
    </button>
  );
}

/** Small link-style button used to jump between sections. */
export function TextButton({ children, onClick, className }: { children: ReactNode; onClick: () => void; className?: string }) {
  return (
    <button type="button" onClick={onClick} className={cn("text-sm font-medium text-accent hover:underline", className)}>
      {children}
    </button>
  );
}

/** Time input styled like the other compact controls. */
export function TimeInput({ value, onChange, label, disabled }: { value: string; onChange: (v: string) => void; label: string; disabled?: boolean }) {
  return (
    <input
      type="time"
      value={value}
      disabled={disabled}
      aria-label={label}
      onChange={(e) => e.target.value && onChange(e.target.value)}
      className="h-8 rounded-md border border-line-strong bg-surface px-2 text-sm tabular text-fg focus:border-accent focus:outline-none disabled:opacity-50"
    />
  );
}
